import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import * as yup from 'yup';
import './StudentSignup.scss';
import { FunctionComponent, useEffect, useState } from 'react';
import InputField from '../../../components/InputField/InputField';
import ButtonStyle from '../../../components/ButtonStyle/ButtonStyle';
import { useLocation, useNavigate } from 'react-router-dom';
import { FETCH_POLICY_NO_CACHE, messages, patterns } from '../../../utils/constants';
import { signUp } from "aws-amplify/auth"
import { CREATE_PTA, CREATE_STUDENT, GET_VIRTUAL_CLASS_LEVELS } from '../api/AdminLoginQuery';
import { useMutation, useQuery } from '@apollo/client';
import ToasterWithMessage from '../../../components/toaster';
import { logout } from '../../../redux/slices/authSlice';
import { useAppDispatch } from '../../../redux/app/hooks';
import { formatPhoneNumber, handleInputWithoutSpaces, handlePhoneNumberInput } from '../../../utils/helper';
import Select, { components, StylesConfig } from 'react-select';
import { ExpandLess, ExpandMore, Padding } from '@mui/icons-material';
import { trackButtonClick } from '../../../firebase/analyticsHelper';
import { ButtonClickEvents } from '../../../firebase/constants/events';

const schema = (loginPage: string) => yup.object().shape({
    fullname: yup
        .string()
        .required(messages?.fullname)
        .trim()
        // .matches(patterns?.alphabetSpace, messages?.validFullName)
        .max(100, 'Full name should not exceed 100 characters'),
    username: loginPage === "ptaSignup" ?
        yup.string().nullable()
     :
        yup
        .string()
        .strict()
        .trim(messages.invalidUsername)
        .required(messages.username)
        .matches(patterns.username, messages.validUsername)
        .matches(patterns.usernameStartWithAlphabets, messages.usernameStartWithAlphabets)
        .matches(patterns.consecutiveDotsDashesUnderscores, messages.noConsecutiveDotsDashesUnderscores)
        .min(6, messages.usernameMinLength)
        .max(50, messages.usernameMaxLength)
        .test('not-only-numbers', messages.invalidUsername, value => {
            if (value) {
                return !patterns.onlyNumbers.test(value);
            }
            return true;
        }),
    email: yup
        .string().required('Email is required')
        .trim()
        .test('is-valid-email', messages.validEmail, function (value) {
            const { path, createError } = this;
            if (value && value.length > 0) {
                return patterns.email.test(value) || createError({ path, message: messages.validEmail });
            }
            return true;
        }),
        phonenumber: yup
        .string()
        .when("$loginpage", {
          is: (val: string) => val === "ptaSignup" || val === "virtualStudentSignup",
          then: (schema) => schema.required("Phone number is required"),
          otherwise: (schema) => schema.optional(),
        })
        .test("is-valid-phoneNumber", messages.validPhoneNumber, function (value) {
          const { path, createError } = this;
      
          // Allow empty values when optional
          if (!value) {
            return true; 
          }
      
          const startsWithPlus = value.startsWith("+");
          console.log(value, "value");
      
          if (!patterns.phoneNumber.test(value)) {
            return createError({ path, message: messages.validPhoneNumber });
          }
          // Check length based on whether it starts with +
          if (startsWithPlus && value.length > 20) {
            return createError({ path, message: messages.phoneNumberLength });
          } else if (!startsWithPlus && value.length > 16) {
            return createError({ path, message: messages.phoneNumberLength16 });
          }
      
          // Regex pattern check
         
      
          return true;
        }),
      
// Applies regex validation only if a value exists

    password: yup
        .string()
        .strict()
        .trim(messages.invalidPassword)
        .required(messages.password)
        .matches(patterns?.password, messages?.validPassword)
        .min(8, messages.passwordMinLength)
        .max(30, messages.passwordMaxLength),
        // .matches(patterns?.upperCasePassword, messages?.passwordOneUpperCase)
        // .matches(patterns?.lowerCasePassword, messages?.passwordOneLowerCase)
        // .matches(patterns?.oneNumbers, messages?.passwordOneNumber)
        // .matches(patterns?.oneSpecialCharacter, messages?.passwordOneSpecialCharater),
    level: loginPage === 'virtualStudentSignup' 
        ? yup.string().required('Level is required') 
        : yup.string(),
    referralcode: loginPage === 'virtualStudentSignup' 
        ? yup.string()
        .test(
            'checkLength',
            'Referral code must be exactly 10 characters',
            (value) => {
                return !value || value.length === 10;
            }
        )
        .test(
            'checkAlphanumeric',
            messages?.validReferralCode,
            (value) => {
                return !value || patterns?.onlyAlphaNumeric?.test(value);
            }
        )
        : yup?.string()
});

interface signupPropsType{
    title: string;
    loginpage: string;
    setSignupSuccess: (val: boolean) => void;
  }

  interface Level {
    value: string;
    label: string;
  }

interface IFormInput{
    fullname: string,
    username: string,
    email?: string | undefined,
    phonenumber?: string,
    password: string,
    level?: string,
    referralcode?: string
}

const StudentSignup: FunctionComponent<signupPropsType> = ({title, loginpage, setSignupSuccess}) => {
    const [disableButton, setDisableButton] = useState<boolean>(false);
    const [levels, setLevels] = useState<Level[]>([]);
    const dispatch = useAppDispatch();
    const {error} = ToasterWithMessage();
    const navigate = useNavigate();
    const methods = useForm({
        resolver: yupResolver(schema(loginpage)),
        context: { loginpage }, 
      });
    const { handleSubmit, setFocus, reset, setValue, trigger, formState:{errors}, setError } = methods;
    const location = useLocation();
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    const customSelectStyles = {
        container: (provided: any) => ({
          ...provided,
          margin: '0',
        }),
        control: (provided: any, state: { selectProps: { menuIsOpen: boolean } }) => ({
          ...provided,
          borderRadius: state?.selectProps?.menuIsOpen ? '8px 8px 0px 0px':'8px',
          border: 'none',
          boxShadow: 'none',
          cursor: 'pointer'
        }),
        valueContainer: (provided: any) => ({
          ...provided,
          padding: '10px 15px',
        }),
        placeholder: (provided: any) => ({
          ...provided,
          fontFamily: 'Inter',
          fontSize: '12px',
          fontWeight: 500,
          color: '#B1B1B1',
        }),
        singleValue: (provided: any) => ({
          ...provided,
          fontFamily: 'Inter',
          fontSize: '12px',
          fontWeight: 500,
          color: '#333',
          textTransform: 'uppercase',
        }),
        menu: (provided: any) => ({
          ...provided,
          marginTop: '0',
          borderRadius: '0 0 8px 8px',
          backgroundColor: '#fff',
          boxShadow: '0px 4px 5px rgba(0, 0, 0, 0.1)',
        }),
        menuList: (provided: any) => ({
          ...provided,
          padding: '8px 0',
        }),
        option: (provided: any, state: { isSelected: boolean; isFocused: boolean }) => ({
          ...provided,
          fontFamily: 'Inter',
          fontSize: '14px',
          fontWeight: 400,
          lineHeight: '14.45px',
          color: state.isSelected ? '#fff' : '#000',
          backgroundColor: state.isSelected
            ? '#0B74BB'
            : state.isFocused
            ? '#f2f2f2'
            : '#fff',
          padding: '7px 15px',
          width:'99%',
          margin: '2px',
          cursor: 'pointer',
          textTransform: 'uppercase',
        }),
        dropdownIndicator: (provided: any, state: { isFocused: boolean }) => ({
          ...provided,
          color: '#ccc',
          paddingRight: '15px'
        }),
        indicatorSeparator: () => ({
          display: 'none',
        }),
      };

    // Custom DropdownIndicator
    const CustomDropdownIndicator = (props: any) => {
        const { selectProps } = props;
        const isMenuOpen = selectProps.menuIsOpen;

        return (
            <components.DropdownIndicator {...props}>
                {isMenuOpen ? <ExpandLess style={{color:"#B1B1B1"}} /> : <ExpandMore style={{ color:"#B1B1B1"}} />}
            </components.DropdownIndicator>
        );
    };

    // Submit the student details
    const onSubmit: SubmitHandler<any> = async (data) => {
        trackButtonClick(ButtonClickEvents?.STUDENT_SIGNUP_BUTTON_CLICK, {
          description : "User clicked the student signup button"
        })

        setDisableButton(true);
        await signUp({
            username: (loginpage === 'ptaSignup' && data?.email) ? data?.email : data?.username,
            password: data?.password,
            options: {
                userAttributes: {
                    email: data?.email,
                    phone_number: data?.phonenumber ?formatPhoneNumber(data?.phonenumber) : "",
                    name: data?.fullname
                }
            }
        }).then(async (datas) => {
            handleCallCreateStudentApi(data, datas?.userId);
        }).catch((err) => {
            setDisableButton(false);
            const errorMessage = err?.message?.toLowerCase();
            if (errorMessage?.includes("invalid phone number format.")) {
                setError("phonenumber", { type: "manual", message: "Invalid phone number format." });
            } else {
                error(err?.message);
            }
        });
        
    }

   //To get level for virtual student signup
    const { data:LevelData, loading:levelLoading } = useQuery(GET_VIRTUAL_CLASS_LEVELS, {
        fetchPolicy: FETCH_POLICY_NO_CACHE,
        onCompleted: async (data) => {
        },
        onError: async (err) => {
            error(err?.message);
        }
    });

    useEffect(() => {
        if (LevelData?.allClassrooms?.nodes) {
            const levelsData = LevelData?.allClassrooms?.nodes.map((level: any) => {
                return {
                    value: level?.cId,
                    label: level?.cOriginalName
                }
            });
            setLevels(levelsData);
        }
    }, [LevelData]);

    //mutation for create a student
    const [createStudent] = useMutation(CREATE_STUDENT, {
        fetchPolicy: FETCH_POLICY_NO_CACHE,
        onCompleted: async (data) => {
            // setSignupSuccess(true);
            // await handleSignIn();
            if (loginpage === 'virtualStudentSignup') {
                navigate('/virtual-school/virtual-student-login?isKaton=virtualSchool', {
                    state: location?.state
                });
            } else {
                navigate('/student-login');
            };
            reset({
                fullname: '',
                username: '',
                password: '',
                email: '',
                phonenumber: '',
                level: '',
                referralcode: ''
            })
        },
        onError: (err) => {
            setDisableButton(false);
            error(err?.message);
        }
    });

    const [createPta] = useMutation(CREATE_PTA, {
        fetchPolicy: FETCH_POLICY_NO_CACHE,
        onCompleted: async (data) => {
            setSignupSuccess(true);
            // await handleSignIn();
            navigate('/pta-login', {
                state: location?.state
            });
            reset({
                fullname: '',
                username: '',
                password: '',
                email: '',
                phonenumber: '',
                level: '',
                referralcode: ''
            })
        },
        onError: async (err) => {
            setDisableButton(false);
        }
    });

    const handleCallCreateStudentApi = (data: IFormInput, userId: string | undefined) => {
        if (loginpage === 'studentSignup' || loginpage === 'virtualStudentSignup') {
            createStudent({
                variables: {
                    input: {
                        userName: data?.username,
                        passWord: data?.password,
                        fullName: data?.fullname,
                        phoneNumber: data?.phonenumber && formatPhoneNumber(data.phonenumber),
                        emailId: data?.email !== '' ? data?.email : null,
                        cognitoId: userId,
                        ...(loginpage === 'virtualStudentSignup' && { cId: data?.level }),
                        ...(loginpage === 'virtualStudentSignup' && { referralCode: data?.referralcode?.trim()?.toUpperCase() }),
                      }
                }
            })
        }
        else if (loginpage === 'ptaSignup') {
            createPta({
                variables: {
                    input: {
                        userName: data?.email !== '' ? data?.email : null,
                        passWord: data?.password,
                        fullName: data?.fullname,
                        phoneNumber: data?.phonenumber && formatPhoneNumber(data.phonenumber),
                        emailId: data?.email !== '' ? data?.email : null,
                        cognitoId: userId
                    }
                }
            })
        }
    }


  useEffect(() => {
    setFocus('fullname');
  },[setFocus])

  useEffect(() => {
    dispatch(logout());
  }, [])

  const handleNavigation = () => {
    trackButtonClick(ButtonClickEvents?.BACK_TO_LOGIN_BUTTON_CLICK, {
      description : "User clicked the Back to Login button"
    });

    if (loginpage === 'ptaSignup') {
        navigate('/pta-login', {
            state: location?.state
        });
    }else if (loginpage === 'virtualStudentSignup') {
        navigate('/virtual-school/virtual-student-login?isKaton=virtualSchool', {
            state: location?.state
        });
    }
    else {
        navigate('/student-login');
    }
  }

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <FormProvider {...methods}>
      <div className="StudentSignupForm">
        <label className="titleStyle">{title}</label>
        <form autoComplete="off" className="signUpForm" onSubmit={handleSubmit(onSubmit)} noValidate>
            <div className='inputFieldsContainer'>
                <InputField label="Full name" name="fullname" type="text" size="fullS" page={"student"}/>
                {(loginpage === 'studentSignup' || loginpage === 'virtualStudentSignup') && <InputField label="Username" name="username" type="text" size="fullS" page={"student"}/>}
                <div className='emailFieldContainer'>
                    <InputField label={"Email"} name="email" type="text" size="fullS" optional={true} page={"student"} onInput={handleInputWithoutSpaces}/>
                    {/* To show optional text, under the field <div className='optionalText'>*optional</div> */}
                </div>
                {loginpage === 'virtualStudentSignup' && (
                <>
                <div className='LevelContainer'>
                <Select
                    id="level"
                    name="level"
                    placeholder="Level"
                    options={levels}
                    isLoading={levelLoading}
                    styles={customSelectStyles}
                    components={{ DropdownIndicator: CustomDropdownIndicator }}
                    onChange={(selectedOption: any) => {
                        setValue('level',selectedOption?.value);
                        trigger('level');
                    }}
                />
                {errors?.level?.message && <span className="error-message">{errors?.level?.message}</span>}
                </div>
                <InputField label={"Referral code*"} name="referralcode" type="text" size="fullS" page={"student"} />
                </>
              )}
                <InputField label={(loginpage === 'ptaSignup' || loginpage === 'virtualStudentSignup') ? "Phone Number" : "Phone Number (optional)"} name="phonenumber" type="text" size="fullS" page={"student"} onInput={handleInputWithoutSpaces}/>
                <InputField label="Password" name="password" type="password" size="fullS" page={"student"}/>
            </div>
            <div className='ctaContainer'>
                <div className='submitButtonContainer'>
                    <ButtonStyle disableButton={disableButton} text="Sign up" type="submit" width={windowWidth > 768 ? "154px" : "420px"} />
                </div>
                <div className='ctaLogin'>
                    <div className='textStyle'>Already have an account </div>
                    <div className='logInCtaStyle' onClick={handleNavigation}>log In</div>
                </div>
            </div>
        </form>
      </div>
    </FormProvider>
  )
}

export default StudentSignup